/* eslint-disable indent */
import { Award } from "lucide-react";
import styled, { css, keyframes } from "styled-components";

import { Button } from "components/atoms/Button";
import { device } from "utils/sizes";
import { colourScheme, darken } from "utils/styling/colours";
import { SubTitle, Text, Title } from "utils/styling/typography.styled";

/* General Components */
export const Container = styled.div`
    margin: 2rem auto;
    padding: 1rem;
    background-color: ${colourScheme.primary};
    color: ${colourScheme.white};
    width: 100%;

    ${device.sm} {
        max-width: 100%;
        padding: 1.5rem;
    }

    ${device.md} {
        max-width: 45rem;
        padding: 2rem;
    }

    ${device.lg} {
        max-width: 60rem;
    }
`;

export const HeaderContainer = styled.div`
    width: 100%;
    margin: 0 auto;
    padding: 1rem;
    background-color: ${colourScheme.primary};
    color: ${colourScheme.white};

    ${device.sm} {
        padding: 1.5rem;
    }

    ${device.md} {
        max-width: 45rem;
        padding: 2rem;
    }

    ${device.lg} {
        max-width: 60rem;
    }
`;

/* Text Styles */
export const TextBold = styled(Text)`
    font-weight: bold;
    color: ${colourScheme.white};
`;

export const TextMuted = styled(Text)`
    color: ${colourScheme.greyLight};
    font-size: 0.875rem;
`;

/* Header Section */
export const Header = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 1.5rem;

    ${device.sm} {
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-start;
    }
`;

export const ProfileInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;

    ${device.sm} {
        flex-direction: row;
        align-items: center;
    }
`;

export const Username = styled(Title)`
    font-size: 1.5rem;
    text-align: center;
    color: ${colourScheme.white};

    ${device.sm} {
        font-size: 2rem;
        text-align: left;
    }
`;

export const UserHandle = styled(Text)`
    font-size: 1rem;
    color: ${colourScheme.white};
    text-align: center;

    ${device.sm} {
        text-align: left;
    }
`;

export const ButtonContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;

    ${device.sm} {
        flex-direction: row;
        align-items: center;
    }
`;

export const EditButton = styled(Button)`
    display: flex;
    align-items: center;
    background-color: ${colourScheme.secondary};
    margin-top: 1rem;

    svg {
        margin-right: 0.5rem;
        color: ${colourScheme.white};
    }

    &:hover {
        background-color: ${darken(colourScheme.secondary, 20)};
    }

    ${device.sm} {
        margin-top: 0;
    }
`;

export const CancelButton = styled(Button)`
    margin-left: 1rem;
    padding: 0.5rem 1rem;
    margin-top: 1rem;

    ${device.sm} {
        margin-top: 0;
    }
`;

/* Bio Section */
export const BioSection = styled.div`
    margin-bottom: 2rem;
    text-align: center;

    ${device.sm} {
        text-align: left;
    }

    .placeholder-bio {
        font-style: italic;
        color: ${colourScheme.grey};
    }
`;

export const BioTitle = styled(SubTitle)`
    font-size: 1.25rem;
    margin-bottom: 1rem;
    color: ${colourScheme.white};

    ${device.sm} {
        font-size: 1.5rem;
    }
`;

export const BioTextField = styled.textarea`
    width: 100%;
    padding: 0.75rem;
    font-size: 1rem;
    background-color: ${colourScheme.primaryLight};
    color: ${colourScheme.white};
    border: none;
    border-radius: 0.5rem;
    box-shadow: inset 0 1px 3px ${colourScheme.shadow};
    resize: vertical;

    &:focus {
        outline: 2px solid ${colourScheme.secondary};
    }
`;

/* Tabs Section */
export const TabsContainer = styled.div`
    padding: 1rem;
    background-color: ${colourScheme.primary};
    border-radius: 0.5rem;
    margin: 0 auto;
`;

export const TabList = styled.div`
    display: flex;
    flex-wrap: wrap;
    border-bottom: 1px solid ${colourScheme.white};
    margin-bottom: 1rem;
`;

interface TabItemProps {
    isActive: boolean;
}

export const TabItem = styled.button.withConfig({
    shouldForwardProp: (prop) => prop !== "isActive",
})<TabItemProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1 0 50%;
    padding: 0.5rem;
    background: none;
    border: none;
    border-bottom: ${({ isActive }) =>
        isActive ? `3px solid ${colourScheme.secondary}` : "none"};
    color: ${({ isActive }) =>
        isActive ? colourScheme.secondary : colourScheme.white};
    cursor: pointer;
    transition: color 0.3s;

    svg {
        margin-right: 0.25rem;
        color: ${({ isActive }) =>
            isActive ? colourScheme.secondary : colourScheme.white};
    }

    &:hover {
        color: ${colourScheme.secondary};
        svg {
            color: ${colourScheme.secondary};
        }
    }

    ${device.sm} {
        flex: none;
        padding: 0.75rem 1.5rem;
    }
`;

/* Following Section */
export const FollowingGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem 0;

    ${device.sm} {
        grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr));
    }

    ${device.md} {
        grid-template-columns: repeat(auto-fill, minmax(17rem, 1fr));
    }
`;

export const FollowingCard = styled.div`
    display: flex;
    align-items: center;
    background-color: ${colourScheme.primaryLight};
    padding: 1rem;
    border-radius: 0.5rem;
    box-shadow: 0px 0.125rem 0.25rem ${colourScheme.shadow};
    transition: background-color 0.3s;
    cursor: pointer;

    &:hover {
        background-color: ${darken(colourScheme.primaryLight, 10)};
    }
`;

export const FollowingAvatar = styled.img`
    width: 2.5rem;
    height: 2.5rem;
    margin-right: 1rem;
    border-radius: 50%;
    background-color: ${colourScheme.greyDark};
`;

export const FollowingInfo = styled.div`
    color: ${colourScheme.white};
`;

export const StatsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;

    ${device.sm} {
        grid-template-columns: repeat(2, 1fr);
    }

    ${device.md} {
        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    }
`;

export const StatCard = styled.div`
    background-color: ${colourScheme.primaryLight};
    padding: 1.5rem;
    border-radius: 0.5rem;
    text-align: center;
    box-shadow: 0px 0.125rem 0.25rem ${colourScheme.shadow};
    transition: transform 0.2s;

    &:hover {
        transform: translateY(-2px);
    }
`;

export const StatValue = styled(Title)`
    font-size: 2rem;
    color: ${colourScheme.secondary};
    margin-bottom: 0.5rem;
`;

export const StatLabel = styled(Text)`
    font-size: 1rem;
    color: ${colourScheme.white};
`;

/* Achievements Section */
export const AchievementGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;

    ${device.sm} {
        grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    }
`;

export const AchievementCard = styled.div`
    background-color: ${colourScheme.primaryLight};
    padding: 1rem;
    border-radius: 0.5rem;
    text-align: center;
    box-shadow: 0px 0.125rem 0.25rem ${colourScheme.shadow};
    transition: transform 0.2s;

    &:hover {
        transform: translateY(-2px);
    }

    ${TextBold} {
        color: ${colourScheme.white};
    }
`;

export const AchievementIcon = styled(Award)`
    color: ${colourScheme.success};
    margin-bottom: 0.5rem;
`;

export const AvatarContainer = styled.div`
    position: relative;
    display: inline-block;
`;

export const EditAvatarButton = styled.button`
    position: absolute;
    bottom: 5px;
    right: 5px;
    background: rgba(0, 0, 0, 0.6);
    border: none;
    border-radius: 50%;
    padding: 8px;
    cursor: pointer;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.3s;

    &:hover {
        background: rgba(0, 0, 0, 0.8);
    }
`;

const subtlePulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.1); }
  50% { box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.2); }
  100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.1); }
`;

const gradientShift = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
`;

export const AvatarWrapper = styled.div<{ subscriptionstyle: any }>`
    position: relative;
    width: 5rem;
    height: 5rem;
    margin-right: 1rem; /* Equivalent to mr-4 */
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    overflow: hidden;

    ${(props) =>
        props.subscriptionstyle.gradientBorder &&
        css`
            background: ${props.subscriptionstyle.gradientBorder};
            background-origin: border-box;
            background-clip: content-box, border-box;
            ${props.subscriptionstyle.animation ===
                "subtlePulse 3s infinite, gradientShift 5s linear infinite" &&
            css`
                animation:
                    ${subtlePulse} 3s infinite,
                    ${gradientShift} 5s linear infinite;
            `}
            ${props.subscriptionstyle.animation === "subtlePulse" &&
            css`
                animation: ${subtlePulse} 3s infinite;
            `}
        `}

    ${(props) =>
        props.subscriptionstyle.borderColor &&
        css`
            border: 3px solid ${props.subscriptionstyle.borderColor};
            overflow: hidden;
            ${props.subscriptionstyle.animation === "subtlePulse" &&
            css`
                animation: ${subtlePulse} 3s infinite;
            `}
        `}
`;

export const UserInfo = styled.div`
    display: flex;
    flex-direction: column;
`;

export const AvatarImage = styled.img`
    width: 4.6rem;
    height: 4.6rem;
    border-radius: 100%;
    object-fit: cover;
`;

export const DefaultAvatar = styled.div`
    width: 100%;
    height: 100%;
    background-color: ${colourScheme.primaryLight};
    display: flex;
    align-items: center;
    justify-content: center;
`;
